import { createContext, createElement, Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_LANG, isLang, langInfo } from "./languages";

// Translations. The English text is the key: t("Create room") shows
// "Raum erstellen" in German, and falls back to the English text when a
// language doesn't have it yet.
// - Placeholders: t("{name} joined", { name }).
// - Plurals: t("{count} point|{count} points", { count }). A dictionary gives
//   "one|other" or an object of plural forms ({ zero, one, two, few, many, other }).
// - Rich text: tx("Tap <b>Start</b>", vars, { b: (c) => <b>{c}</b> }) returns
//   React nodes. <b>, <i>, <em> and <strong> work without being listed.
// - Messages saved in the database (chat, game banners) store the English
//   key and values with msg(), and every player sees them in their own
//   language (see showMsg).

const STORAGE_KEY = "partycade.lang";
const dicts = { en: {} };
const loaders = {
  de: () => import("./de"),
  fr: () => import("./fr"),
  ar: () => import("./ar"),
};

let current = DEFAULT_LANG;
export const currentLang = () => current;

export function detectLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch {}
  const wanted = typeof navigator === "undefined" ? [] : navigator.languages || [navigator.language];
  for (const l of wanted) {
    const code = String(l || "")
      .slice(0, 2)
      .toLowerCase();
    if (isLang(code)) return code;
  }
  return DEFAULT_LANG;
}

// Downloads a language's dictionary (English needs none).
export async function loadLang(code) {
  if (!dicts[code] && loaders[code]) dicts[code] = (await loaders[code]()).default;
  return code;
}

// For tests and tools.
export const setDictionary = (code, dict) => {
  dicts[code] = dict;
};

const pluralRules = {};
function pluralForm(lang, n) {
  pluralRules[lang] = pluralRules[lang] || new Intl.PluralRules(lang);
  return pluralRules[lang].select(n);
}

function pick(lang, text, vars) {
  let msg = dicts[lang]?.[text];
  if (msg == null || msg === "") {
    msg = text;
    lang = "en";
  }
  if (vars && typeof vars.count === "number") {
    if (typeof msg === "object") return msg[pluralForm(lang, vars.count)] ?? msg.other;
    if (msg.includes("|")) {
      const [one, other] = msg.split("|");
      return pluralForm(lang, vars.count) === "one" ? one : other;
    }
  } else if (typeof msg === "object") return msg.other;
  return msg;
}

// Values can themselves be translatable: { t: "Doodle Deck" } or msg(...).
function resolve(lang, v) {
  return v && typeof v === "object" && typeof v.k === "string" ? translate(lang, v.k, v.v) : v;
}

export function translate(lang, text, vars) {
  const msg = pick(lang, text, vars);
  if (!vars) return msg;
  return msg.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(resolve(lang, vars[name]) ?? "") : m));
}

// Translates with whatever language the site is showing (for code outside components).
export const tt = (text, vars) => translate(current, text, vars);

// Marks a string for translation where it's shown later, e.g. a list of labels.
export const mark = (text) => text;

// A message to save in the database: { k: English text, v: values }. Values
// can be msg()s too, like a game's name.
export const msg = (text, vars) => (vars ? { k: text, v: vars } : { k: text });

// Shows a saved message (or plain text) in a language.
export const showMsg = (lang, m) => (m && typeof m === "object" && typeof m.k === "string" ? translate(lang, m.k, m.v) : m ?? "");

const DEFAULT_TAGS = {
  b: (c, k) => createElement("b", { key: k }, c),
  strong: (c, k) => createElement("strong", { key: k }, c),
  i: (c, k) => createElement("i", { key: k }, c),
  em: (c, k) => createElement("em", { key: k }, c),
};

// Turns "Tap <b>{what}</b> now" into React nodes. Values can be nodes.
export function translateRich(lang, text, vars = {}, tags = {}) {
  const src = pick(lang, text, vars);
  const all = { ...DEFAULT_TAGS, ...tags };
  let key = 0;
  const fill = (s) =>
    s.split(/(\{\w+\})/).map((part) => {
      const m = /^\{(\w+)\}$/.exec(part);
      if (!m || !(m[1] in vars)) return part;
      const v = resolve(lang, vars[m[1]]);
      return typeof v === "object" && v !== null ? createElement(Fragment, { key: `v${key++}` }, v) : String(v ?? "");
    });
  const out = [];
  const re = /<(\w+)>([\s\S]*?)<\/\1>|<(\w+)\s*\/>/g;
  let last = 0;
  let m;
  while ((m = re.exec(src))) {
    out.push(...fill(src.slice(last, m.index)));
    const name = m[1] || m[3];
    const render = all[name];
    if (m[3]) out.push(render ? render(null, `t${key++}`) : createElement("br", { key: `t${key++}` }));
    else out.push(render ? render(fill(m[2]), `t${key++}`) : createElement(Fragment, { key: `t${key++}` }, fill(m[2])));
    last = re.lastIndex;
  }
  out.push(...fill(src.slice(last)));
  return out.filter((x) => x !== "");
}

// ---- React --------------------------------------------------------------
const I18nContext = createContext(null);

function applyToPage(code) {
  current = code;
  if (typeof document === "undefined") return;
  document.documentElement.lang = code;
  document.documentElement.dir = langInfo(code).dir;
}

export function I18nProvider({ initial = DEFAULT_LANG, children }) {
  const [lang, setLangState] = useState(() => {
    applyToPage(initial);
    return initial;
  });

  const setLang = useCallback(async (code) => {
    if (!isLang(code)) return;
    await loadLang(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {}
    applyToPage(code);
    setLangState(code);
  }, []);

  useEffect(() => applyToPage(lang), [lang]);

  const value = useMemo(
    () => ({
      lang,
      dir: langInfo(lang).dir,
      setLang,
      t: (text, vars) => translate(lang, text, vars),
      tx: (text, vars, tags) => translateRich(lang, text, vars, tags),
      show: (m) => showMsg(lang, m),
    }),
    [lang, setLang]
  );
  return createElement(I18nContext.Provider, { value }, children);
}

const fallback = {
  lang: DEFAULT_LANG,
  dir: "ltr",
  setLang: () => {},
  t: (text, vars) => translate(DEFAULT_LANG, text, vars),
  tx: (text, vars, tags) => translateRich(DEFAULT_LANG, text, vars, tags),
  show: (m) => showMsg(DEFAULT_LANG, m),
};

export const useI18n = () => useContext(I18nContext) || fallback;
export const useT = () => useI18n().t;
